import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import Modal from '../Modal/Modal';
import { useI18n } from '../../i18n/I18nContext';

/**
 * ConfirmDialog — replaces `window.confirm` / `alert` and the sign-out dialog (spec §3.13): a
 * 56px icon tile, a question as the title, one line saying what happens, then the two buttons.
 * Cancel takes the focus (the safe choice); Esc and the backdrop cancel.
 *
 * Declarative:
 *   <ConfirmDialog
 *     open={askDelete}
 *     title="Kursni o'chirasizmi?"
 *     description="Darslar va natijalar ham o'chib ketadi."
 *     confirmLabel="O'chirish"
 *     tone="danger"
 *     onConfirm={removeCourse}
 *     onCancel={() => setAskDelete(false)}
 *   />
 *
 * Imperative — a drop-in for `if (!window.confirm(...)) return;`:
 *   const [confirm, confirmDialog] = useConfirmDialog();
 *   ...
 *   async function remove() {
 *     if (!(await confirm({ title, description, confirmLabel: t('ui.delete'), tone: 'danger' }))) return;
 *     ...
 *   }
 *   return (<>… {confirmDialog}</>);
 *
 * tone: 'danger' (red icon tile + filled red confirm) | 'primary' (yellow confirm) | 'neutral' (ink confirm).
 * `loading` shows the spinner in the confirm button and keeps the dialog from closing itself.
 */
function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  icon,
  loading = false,
  onConfirm,
  onCancel,
  onClose,
}) {
  const { t } = useI18n();
  const uid = useId();
  const cancel = onCancel || onClose;
  const Icon = tone === 'danger' ? AlertTriangle : HelpCircle;
  const iconTone = tone === 'danger' ? 'danger' : tone === 'primary' ? 'brand' : 'neutral';
  const confirmClass = tone === 'danger'
    ? 'ds-btn-danger-fill'
    : tone === 'primary'
      ? 'ds-btn-primary'
      : 'ds-btn-ink';

  return (
    <Modal
      open={open}
      onClose={() => { if (!loading && cancel) cancel(); }}
      size="sm"
      hideClose
      className="ui-confirm"
      labelledBy={`${uid}-title`}
      describedBy={description ? `${uid}-desc` : undefined}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={cancel} disabled={loading} data-autofocus>
            {cancelLabel || t('ui.cancel')}
          </button>
          <button
            type="button"
            className={`ds-btn ${confirmClass}${loading ? ' is-loading' : ''}`}
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading || undefined}
          >
            {confirmLabel || (tone === 'danger' ? t('ui.delete') : t('ui.confirmAction'))}
          </button>
        </>
      )}
    >
      <span className="ui-icon-chip ui-icon-chip--lg ui-confirm__icon" data-tone={iconTone} aria-hidden="true">
        {icon || <Icon />}
      </span>
      <h2 id={`${uid}-title`} className="ui-confirm__title">{title || t('ui.confirmTitle')}</h2>
      {description && <p id={`${uid}-desc`} className="ui-confirm__desc">{description}</p>}
    </Modal>
  );
}

/**
 * Promise-based confirmation: `const [confirm, dialog] = useConfirmDialog()`. `confirm(options)`
 * opens the dialog with the ConfirmDialog props (title, description, confirmLabel, cancelLabel,
 * tone, icon) and resolves true when confirmed, false when cancelled. Render `dialog` once in
 * the component's JSX. A pending question resolves false if the component unmounts.
 */
export function useConfirmDialog() {
  const [pending, setPending] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((answer) => {
    const resolve = resolver.current;
    resolver.current = null;
    setPending(null);
    if (resolve) resolve(answer);
  }, []);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    // A second question while one is open cancels the first.
    if (resolver.current) resolver.current(false);
    resolver.current = resolve;
    setPending(options);
  }), []);

  useEffect(() => () => {
    if (resolver.current) resolver.current(false);
  }, []);

  const dialog = pending ? (
    <ConfirmDialog
      open
      {...pending}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return [confirm, dialog];
}

export default ConfirmDialog;
